/**
 * The map dialog: the map, on request, in a modal.
 *
 * @remarks
 * The map used to render inline between the transcript and the input, where it
 * grew with the discovered world and pushed the prompt down the page — a
 * reference panel competing with the thing the player is actually doing. It is
 * now behind a button, which is the right shape for it: consulted, not
 * inhabited.
 *
 * **Why a native `<dialog>`.** `showModal()` gives focus trapping, `Escape` to
 * dismiss, the top layer, an inert background and focus restoration to the
 * trigger — all behaviors a hand-rolled `role="dialog"` has to re-implement
 * and usually re-implements incompletely. The one cost is that jsdom does not
 * implement `showModal`, so the shared jsdom setup shims it; the REAL behavior
 * is covered by the Playwright + axe specs, which run in Chromium.
 *
 * The component owns only "is it open" — the map itself stays
 * {@link MapPanelComponent}, pure and prop-driven, so there is still exactly
 * one thing that knows how to draw a map.
 *
 * @packageDocumentation
 */

import { type MapGraph } from '@orpheus/api-contracts';
import { type MapLayout } from '@orpheus/engine-runtime';
import { type ReactNode, useState } from 'react';

import { MapPanelComponent, type MapPanelComponentProps } from '../map-panel/map-panel-component.js';
import { ModalCornerCloseComponent } from '../modal-dialog/modal-corner-close-component.js';
import { showModalWhileMounted } from '../modal-dialog/show-modal-while-mounted.js';

/** Props for {@link MapDialogComponent}. */
export interface MapDialogComponentProps {
	/** Links the grid cannot draw honestly — passed through to the panel. */
	readonly annotations: MapGraph['annotations'];
	/** D211: the style chooser, where the pack offers a choice — passed through to the panel, under its heading. */
	readonly chooser?: ReactNode;
	/** The engine's positioned drawing primitives, in the style the player is looking at (D209). */
	readonly layout: MapLayout;
	/** D210: a line above the figure when the drawing is not the one the player asked for — passed through to the panel. */
	readonly notice?: string | undefined;
	/** D210: the shell's renderer for a `scene` layout — passed through to the panel. */
	readonly renderScene?: MapPanelComponentProps['renderScene'];
	/** What the pack calls its map (D189) — the panel heading and the figure's name. The trigger stays the chrome's one word, `Map`: it is a control, not the map. */
	readonly title: string;
	/** How many rooms the player has walked — shown on the trigger and the panel heading. */
	readonly visitedCount: number;
	/**
	 * Called once the dialog has closed, by any route — the Close button, the corner ✕ or
	 * `Escape`. The native dialog returns focus to the trigger; the shell uses
	 * this to send it on to the directive line instead, so a player who
	 * consulted the map can type the next move without a click (Mike,
	 * 2026-09-10). Optional: without it, the native behavior stands.
	 */
	readonly onDismissed?: () => void;
}

/**
 * Render the map trigger and its modal dialog.
 *
 * @param props - {@link MapDialogComponentProps}
 * @returns The trigger button and the dialog it opens
 */
export const MapDialogComponent = ({ annotations, chooser, layout, notice, onDismissed, renderScene, title, visitedCount }: MapDialogComponentProps) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<button
				aria-haspopup="dialog"
				// Named in full for assistive tech; the visible chrome stays compact.
				// D191: the same readout the panel heading uses, so the control and
				// the thing it opens name one fact one way.
				aria-label={`Map — locations walked: ${String(visitedCount)}`}
				className="map-dialog__trigger"
				onClick={() => {
					setIsOpen(true);
				}}
				type="button"
			>
				<MapIcon />
				<span className="map-dialog__trigger-label">Map</span>
				<span aria-hidden="true" className="map-dialog__trigger-count">
					{visitedCount}
				</span>
			</button>
			{isOpen ? (
				<dialog
					aria-labelledby="map-panel-heading"
					className="map-dialog"
					// The ONE route out. Escape closes the element natively; the Close
					// button closes it too (below) rather than dropping the flag first —
					// while the modal is up the page behind it is inert, so focus sent
					// to the directive line then would be refused. Only once the
					// element has closed (and the browser has put focus back on the
					// trigger) can the shell send it on, which is what `onDismissed`
					// is for.
					onClose={() => {
						setIsOpen(false);
						onDismissed?.();
					}}
					// The element exists exactly while the map is open, so the modal is
					// opened as it attaches and closed as it detaches — the one ref every
					// dialog here shares, and why it must be that one function (D172).
					ref={showModalWhileMounted}
				>
					<div className="map-dialog__body">
						<MapPanelComponent annotations={annotations} chooser={chooser} layout={layout} notice={notice} renderScene={renderScene} title={title} visitedCount={visitedCount} />
						<button
							className="map-dialog__close"
							onClick={(event) => {
								event.currentTarget.closest('dialog')?.close();
							}}
							type="button"
						>
							Close
						</button>
					</div>
					<ModalCornerCloseComponent label="Close map" />
				</dialog>
			) : null}
		</>
	);
};

/**
 * The trigger's folded-map glyph. Decorative — the button is named by its
 * visible label, so the icon is hidden from assistive technology.
 *
 * @returns The icon
 */
const MapIcon = () => (
	<svg
		aria-hidden="true"
		className="map-dialog__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
		<path d="M9 4v13" />
		<path d="M15 6.5v13" />
	</svg>
);
